import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import axios from "@/libs/axios";
import { debounce } from "lodash";
import moment from "moment-timezone";
import i18n from "@/libs/i18n";

export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();

  const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-");

  const tableColumns = [
    { label: "axis_body_cam.recordings.container_name", key: "container_name" },
    { label: "axis_body_cam.recordings.user", key: "user" },
    { label: "axis_body_cam.recordings.device", key: "device" },
    { label: "axis_body_cam.recordings.start_time", key: "start_time", sortable: true, formatter: formatDate },
    { label: "axis_body_cam.recordings.stop_time", key: "stop_time", formatter: formatDate },
    { label: "axis_body_cam.recordings.status", key: "status" },
    { label: "axis_body_cam.recordings.files", key: "files" },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("start_time");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("axis_body_cam_recordings/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
      })
      .then((response) => {
        callback(response.data);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  const update = (data) => {
    currentPage.value = data;
  };

  const refPlayer = ref(null);
  const busy = ref({});

  const notifyError = (title) =>
    toast({
      component: ToastificationContent,
      props: { title, icon: "AlertTriangleIcon", variant: "danger" },
    });

  const absoluteUrl = (path) => `${String(axios.defaults.baseURL || "").replace(/\/$/, "")}${path}`;

  const startDownload = (path) => {
    const a = document.createElement("a");
    a.href = absoluteUrl(path);
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    toast({
      component: ToastificationContent,
      props: {
        title: i18n.t("axis_body_cam.recordings.download_started"),
        icon: "DownloadIcon",
        variant: "success",
      },
    });
  };

  const withLinks = async (recording, callback) => {
    if (busy.value[recording.id]) return;

    busy.value = { ...busy.value, [recording.id]: true };

    try {
      callback(await store.dispatch("axis_body_cam_recordings/getLinks", recording.id));
    } catch (e) {
      notifyError(i18n.t("axis_body_cam.recordings.links_error"));
    } finally {
      busy.value = { ...busy.value, [recording.id]: false };
    }
  };

  const downloadFile = (recording, file) =>
    withLinks(recording, (links) => {
      const link = links.files.find((item) => item.id === file.id);

      if (link) startDownload(link.download_url);
    });

  const downloadAll = (recording) =>
    withLinks(recording, (links) => {
      if (links.zip_url) startDownload(links.zip_url);
    });

  const playRecording = (recording) =>
    withLinks(recording, (links) => {
      if (!links.playlist.length) {
        notifyError(i18n.t("axis_body_cam.recordings.no_videos"));
        return;
      }

      refPlayer.value.open(
        recording,
        links.playlist.map((part) => ({ ...part, stream_url: absoluteUrl(part.stream_url) }))
      );
    });

  const hasVideo = (recording) => (recording.files || []).some((file) => /\.(mkv|mp4|webm|mov)$/i.test(file.object_name));

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  return {
    tableColumns,
    perPage,
    perPageOptions,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    update,
    downloadFile,
    downloadAll,
    playRecording,
    hasVideo,
    refPlayer,
    busy,
  };
}
